import { createSelector } from "reselect";
import createCachedSelector from "re-reselect";
import _get from "lodash/get";
import _values from "lodash/values";
import _orderBy from "lodash/orderBy";
import _groupBy from "lodash/groupBy";
import _includes from "lodash/includes";
import _every from "lodash/every";
import _reduce from "lodash/reduce";
import _find from "lodash/find";
import _map from "lodash/map";
import _isArray from "lodash/isArray";
import _partition from "lodash/partition";
import _uniq from "lodash/uniq";
import _uniqBy from "lodash/uniqBy";

import { getDistance } from "./utils";

const authLat = (state) => state.auth.lat;
const authLng = (state) => state.auth.lng;
const authMessages = (state) => state.auth.messages;
const authIsPremium = (state) => state.auth.isPremium;
const authUser = (state) => state.auth.user;
//
const modelsOnline = (state) => state.models.online;
const modelsOffline = (state) => state.models.offline;
const modelsFavorite = (state) => state.models.favorite;
const modelsEntities = (state) => state.models.entities;
const modelsUnavailable = (state) => state.models.unavailable;
export const getModelId = (state) => state.models.active;
//
const brandEntities = (state) => state.brands.entities;
const ratesEntities = (state) => state.rates.entities;
const servicesEntities = (state) => state.services.entities;
const reviewsEntities = (state) => state.reviews.entities;
//
const filtersValues = (state) => state.filters.values;
const filtersOrderBy = (state) => state.filters.orderBy;
const filtersOrderDir = (state) => state.filters.orderDir;
//
const bookingDuration = (state) => state.booking.duration;
const bookingGenderId = (state) => state.booking.genderId;
const bookingOutcall = (state) => state.booking.outcall;
export const getUserServices = (state) => state.booking.services;
//
const reviewsModelId = (state) => state.reviews.modelId;
const reviewsNoMores = (state) => state.reviews.noMores;
const reviewsPages = (state) => state.reviews.pages;

// Intl
const intlCities = (state) => state.intl.cities;
const intlCityId = (state) => state.intl.cityId;
const intlRegionId = (state) => state.intl.regionId;

// Places
const placesEntities = (state) => state.places.entities;
const placesResult = (state) => state.places.result;

// Locations
const locationsEntities = (state) => state.locations.entities;
const locationsActive = (state) => state.locations.active;
const locationsPreview = (state) => state.locations.preview;
export const getLocationFee = (state) => state.locations.fee;

// Videos
export const getVideos = createSelector(
  [
    (state) => state.models.entities,
    (state) => state.videos.byId,
    (state) => state.videos.result,
  ],
  (modelsById, byId, result) => {
    const videos = result.map((id) => {
      const video = { ...byId[id] };

      video.model = modelsById[video.model];

      return video;
    });

    // created_at DESC
    return videos.sort((a, b) => b.createdAt - a.createdAt);
  }
);

// Cities
export const getCity = createSelector(
  [intlCities, intlCityId],
  (cities, cityId) => _find(cities, (city) => city.id === cityId)
);

export const getDiscounts = createSelector(
  [getCity, bookingOutcall],
  (city, outcall) => {
    if (outcall) {
      return city.discounts_outcall || [];
    }

    return city.discounts_incall || [];
  }
);

export const getRegions = createSelector([intlCities], (cities) => {
  let regions;

  regions = cities.map((city) => city.region);
  regions = _uniqBy(regions, (region) => region.id);
  regions = _orderBy(regions, ["name"], ["asc"]);

  return regions;
});

export const getCities = createSelector(
  [intlCities, intlRegionId],
  (cities, regionId) => cities.filter((city) => city.region.id === regionId)
);

export const getPlaces = createSelector(
  [placesEntities, placesResult],
  (places, result) => result.map((id) => places[id])
);

export const getPreviewLocation = createSelector(
  [locationsEntities, locationsPreview],
  (locations, id) => {
    return locations[id];
  }
);

export const getLocation = createSelector(
  [locationsEntities, locationsActive],
  (locations, id) => {
    return locations[id];
  }
);

const getModelsAvailable = createSelector(
  [modelsEntities, modelsOnline, modelsUnavailable],
  (models, online, unavailable) => {
    return online
      .filter((id) => !_includes(unavailable, id))
      .map((id) => models[id]);
  }
);

export const getUserLocation = createSelector(
  [getLocation, getPreviewLocation, authLat, authLng],
  (active, preview, lat, lng) => {
    const location = active || preview;

    return {
      lat: _get(location, "lat", lat || null),
      lng: _get(location, "lng", lng || null),
    };
  }
);

const getModelsBookable = createSelector(
  [
    getModelsAvailable,
    ratesEntities,
    bookingDuration,
    bookingOutcall,
    getUserLocation,
    getCity,
  ],
  (models, rates, duration, outcall, location, city) => {
    return models.filter((model) =>
      _isModelBookable(model, rates, duration, outcall, location, city)
    );
  }
);

const getModelsUnbookable = createSelector(
  [
    getModelsAvailable,
    ratesEntities,
    bookingDuration,
    bookingOutcall,
    getUserLocation,
    getCity,
  ],
  (models, rates, duration, outcall, location, city) =>
    models.filter((model) =>
      _isModelUnbookable(model, rates, duration, outcall, location, city)
    )
);

export const getOrderBy = createSelector(
  [filtersOrderBy, filtersOrderDir],
  (by, dir) => ({ by, dir })
);

export const getModelsOnline = createSelector(
  [
    getModelsBookable,
    getModelsUnbookable,
    getOrderBy,
    ratesEntities,
    bookingDuration,
    bookingOutcall,
    getUserLocation,
    intlCityId,
  ],
  (bookable, unbookable, order, rates, duration, outcall, location, cityId) => {
    bookable = order.by
      ? orderModels(
          bookable,
          order,
          rates,
          duration,
          outcall,
          location.lat,
          location.lng
        )
      : bookable;

    bookable = _orderBy(bookable, ["city_id"], [cityId]);

    unbookable = order.by
      ? orderModels(
          unbookable,
          order,
          rates,
          duration,
          outcall,
          location.lat,
          location.lng
        )
      : unbookable;

    return [...bookable, ...unbookable];
  }
);

export const getModelsOffline = createSelector(
  [modelsEntities, modelsOffline],
  (models, offline) => {
    return _orderBy(
      offline.map((id) => models[id]),
      ["offlineAt"],
      ["desc"]
    );
  }
);

export const getModelsFavorite = createSelector(
  [modelsFavorite, modelsEntities, bookingGenderId],
  (ids, models, genderId) =>
    ids
      .map((id) => models[id])
      .filter((model) => model && model.genderId === genderId)
);

export const getVisibleBrands = createSelector(
  [brandEntities, modelsEntities, intlCityId],
  (brandsById, models, cityId) => {
    const brandIds = _values(models)
      .filter((model) => model.brand && model.city.id === cityId)
      .map((model) => model.brand);

    const brands = _uniq(brandIds)
      .map((id) => brandsById[id])
      .filter(Boolean);

    return _orderBy(brands, ["name"], ["asc"]);
  }
);

export const getModel = (state, modelId = null) =>
  state.models.entities[_resolveModelId(state, modelId)];

export const isModelOnline = createCachedSelector(
  [getModel, modelsOnline],
  (model, onlines) => _includes(onlines, model.id)
)(_resolveModelId);

export const isModelFavorite = createCachedSelector(
  [getModel, modelsFavorite],
  (model, favorites) => _includes(favorites, model.id)
)(_resolveModelId);

export const isModelBookable = createCachedSelector(
  [
    getModel,
    ratesEntities,
    bookingDuration,
    bookingOutcall,
    getUserLocation,
    getCity,
  ],
  (model, rates, duration, outcall, location, city) =>
    _isModelBookable(model, rates, duration, outcall, location, city)
)(_resolveModelId);

// export const isModelUnbookable = createCachedSelector(
//   [getModel, ratesEntities, bookingDuration, bookingOutcall, getUserLocation],
//   (model, rates, duration, outcall, location) =>
//     _isModelUnbookable(model, rates, duration, outcall, location)
// )(_resolveModelId);

export const getModelDistance = createCachedSelector(
  [getModel, bookingOutcall, getUserLocation],
  (model, outcall, location) => {
    if (!location.lat) {
      return null;
    }

    return outcall
      ? _getOutcallDistance(model, location.lat, location.lng)
      : _getIncallDistance(model, location.lat, location.lng);
  }
)(_resolveModelId);

export const getModelBrand = createCachedSelector(
  [getModel, brandEntities],
  (model, brands) => brands[model.brand]
)(_resolveModelId);

export const getModelRates = createCachedSelector(
  [getModel, ratesEntities],
  (model, rates) => {
    return model.rates.map((id) => rates[id]);
  }
)(_resolveModelId);

export const getModelOutcalls = createCachedSelector(
  [getModelRates, bookingOutcall],
  (rates, outcall) => {
    return rates.filter((rate) => Boolean(rate.outcall));
  }
)(_resolveModelId);

export const getModelIncalls = createCachedSelector(
  [getModelRates, bookingOutcall],
  (rates, outcall) => {
    return rates.filter((rate) => !Boolean(rate.outcall));
  }
)(_resolveModelId);

export const getModelPerHour = createCachedSelector(
  [getModelRates, bookingDuration, bookingOutcall],
  (rates, duration, outcall) => _getModelPerHour(rates, duration, outcall)
)(_resolveModelId);

export const getModelServices = createCachedSelector(
  [getModel, servicesEntities],
  (model, services) => (model.services || []).map((id) => services[id])
)(_resolveModelId);

export const getModelPaidServices = createCachedSelector(
  [getModelServices],
  (services) => services.filter((_serv) => _serv.paid)
)(_resolveModelId);

export const getModelReviews = createCachedSelector(
  [getModel, reviewsEntities],
  (model, reviews) => (model.reviews || []).map((id) => reviews[id])
)(_resolveModelId);

export const getModelRate = createCachedSelector(
  [getModelRates, bookingDuration, bookingOutcall],
  (rates, duration, outcall) =>
    _find(rates.filter(Boolean), (rate) => isRate(rate, duration, outcall))
)(_resolveModelId);

export const getModelDiscount = createCachedSelector(
  [getModelRate, getDiscounts],
  (rate, discounts) => {
    if (rate) {
      return discounts[rate.duration] || 0;
    }

    return 0;
  }
)(_resolveModelId);

export const getBookingServices = createSelector(
  [servicesEntities, getUserServices, getModelId],
  (services, userServices, modelId) => {
    return userServices.map((id) => services[`m${modelId}-s${id}`]);
  }
);

const getServicesPrice = createSelector(
  [getBookingServices, getModelRate],
  (services, modelRate) => {
    return _reduce(
      services,
      (sum, _serv) => {
        const price =
          typeof _serv.multiplier !== "undefined"
            ? _serv.multiplier * modelRate.amount
            : _serv.price;
        return sum + price;
      },
      0
    );
  }
);

// MAX DATE
import { maxAdvanceHours, maxAdvanceHoursLoggedIn } from "./config";
import addHours from "date-fns/add_hours";

const getCityId = (state) => state.intl.cityId;
const isPremium = (state) => state.auth.isPremium;
const _getMaxDate = (state) => state.booking.maxDate;
const getMinDate = (state) => state.booking.minDate;
const getBookingDate = (state) => state.booking.date;

export const isComplimentaryCity = createSelector(
  [getCityId],
  (cityId) => cityId !== 1
);

export const getMaxDate = createSelector(
  [isComplimentaryCity, isPremium, _getMaxDate],
  (isComplimentaryCity, isPremium, maxDate) => {
    if (isComplimentaryCity || isPremium) {
      return addHours(maxDate, maxAdvanceHoursLoggedIn - maxAdvanceHours);
    }
    return maxDate;
  }
);

// MAX HOURS (in advance)
export const getMaxHours = createSelector(
  [isComplimentaryCity, isPremium],
  (isComplimentaryCity, isPremium) => {
    if (isComplimentaryCity || isPremium) {
      return maxAdvanceHoursLoggedIn;
    }
    return maxAdvanceHours;
  }
);

// MIN/MAX DATE/TIME
import isAfter from "date-fns/is_after";
import isBefore from "date-fns/is_before";
import isEqual from "date-fns/is_equal";
import { __makeTemplateObject } from "tslib";

export const isDateMax = createSelector(
  [getBookingDate, getMaxDate],
  (date, max) => isEqual(date, max) || isAfter(date, max)
);

export const isAfterDateMax = createSelector(
  [getBookingDate, getMaxDate],
  (date, max) => isAfter(date, max)
);

export const isDateMin = createSelector(
  [getBookingDate, getMinDate],
  (date, min) => isBefore(date, min) || isEqual(date, min)
);

export const isBeforeDateMin = createSelector(
  [getBookingDate, getMinDate],
  (date, min) => isBefore(date, min)
);

export const getActiveFilterValues = createSelector(
  [filtersValues],
  (filters) =>
    _values(filters).filter(
      (val) => (!_isArray(val) && val) || (_isArray(val) && val.length)
    )
);

// Reviews
export const getReviewsNoMore = createSelector(
  [reviewsModelId, reviewsNoMores],
  (modelId, noMores) =>
    noMores[modelId] === undefined ? false : noMores[modelId]
);

export const getReviewsPage = createSelector(
  [reviewsModelId, reviewsPages],
  (modelId, pages) => pages[modelId] || 1
);

export const getUnread = createSelector(
  [authMessages],
  (messages) => messages.length
);

export const isPremiumIn = createSelector(
  [authIsPremium, authUser, intlCityId],
  (isPremium, user, cityId) => isPremium && _includes(user.cities, cityId)
);

export const getTotalPrice = createCachedSelector(
  [
    getModelDiscount,
    getModelRate,
    getLocationFee,
    getServicesPrice,
    isPremiumIn,
  ],
  (discount, modelRate, fee, servicesPrice, isPremium) => {
    let total = modelRate.amount + fee + servicesPrice;

    if (isPremium) {
      total -= discount;
    }

    return total;
  }
)(_resolveModelId);

// ----------------------------------------------------------------------------------------------------------------------------
//  Helpers
// ----------------------------------------------------------------------------------------------------------------------------
function orderModels(models, order, rates, duration, outcall, lat, lng) {
  if (order.by === "score") {
    const [included, excluded] = _partition(
      models,
      (model) => model.ratingsCount > 2
    );

    const ordered = _orderBy(included, (model) => model.score, [order.dir]);

    return [...ordered, ...excluded];
  }

  return _orderBy(
    models,
    [
      (model) => {
        if (order.by === "price") {
          const _rates = model.rates.map((id) => rates[id]);
          const rate = _getModelRate(_rates, duration, outcall);

          if (rate) {
            return rate.amount;
          }

          return _getModelPerHour(_rates, duration, outcall);
        } else if (order.by === "distance") {
          const distance = outcall
            ? _getOutcallDistance(model, lat, lng)
            : _getIncallDistance(model, lat, lng);

          // https://www.space.com/17638-how-big-is-earth.html
          return distance || 40075 / 2;
        } else if (order.by === "rating") {
          return model.ratingsCount;
        }

        return model[order.by];
      },
    ],
    [order.dir]
  );
}

function isRate(rate, duration, outcall) {
  return rate.duration === duration && Boolean(rate.outcall) === outcall;
}

function isRateBefore(rate, duration, outcall) {
  return rate.duration < duration && Boolean(rate.outcall) === outcall;
}

function isRateAfter(rate, duration, outcall) {
  return rate.duration > duration && Boolean(rate.outcall) === outcall;
}

function _getModelRate(rates, duration, outcall) {
  return _find(rates.filter(Boolean), (rate) =>
    isRate(rate, duration, outcall)
  );
}

function _getModelPerHour(rates, duration, outcall) {
  const rate1 = _find(rates, (rate) => isRateBefore(rate, duration, outcall));
  const rate2 = _find(rates, (rate) => isRateAfter(rate, duration, outcall));

  let amount;
  let minutes;

  if (rate1 && rate2) {
    amount = rate1.amount + rate2.amount;
    minutes = rate1.duration + rate2.duration;
  } else if (rate1) {
    amount = rate1.amount;
    minutes = rate1.duration;
  } else if (rate2) {
    amount = rate2.amount;
    minutes = rate2.duration;
  }

  if (!amount) {
    return null;
  }

  return Math.round(amount / (minutes / 60));
}

function _resolveModelId(state, modelId) {
  return modelId || state.models.active;
}

function _getOutcallDistance(model, lat, lng) {
  return model.lat && model.lng && lat && lng
    ? getDistance(model.lat, model.lng, lat, lng)
    : null;
}

function _getIncallDistance(model, lat, lng) {
  return model.location && lat && lng
    ? getDistance(model.location.lat, model.location.lng, lat, lng)
    : null;
}

function _isModelBookable(model, rates, duration, outcall, location, city) {
  // Rate exists?
  const _rates = model.rates.map((id) => rates[id]);
  const rate = _getModelRate(_rates, duration, outcall);

  // Distance too far?
  const { lat, lng } = location;
  const distance = outcall
    ? _getOutcallDistance(model, lat, lng)
    : _getIncallDistance(model, lat, lng);

  return (
    rate !== undefined &&
    (!distance || (distance && distance <= city.booking_distance))
  );
}

function _isModelUnbookable(model, rates, duration, outcall, location, city) {
  // Rate exists?
  const _rates = model.rates.map((id) => rates[id]);
  const rate = _getModelRate(_rates, duration, outcall);

  // Distance too far?
  const { lat, lng } = location;
  const distance = outcall
    ? _getOutcallDistance(model, lat, lng)
    : _getIncallDistance(model, lat, lng);

  return rate === undefined || (distance && distance > city.booking_distance);
}
